import {
  Flex,
  Link,
  IconButton,
  VStack,
  useDisclosure,
  Box,
  HStack,
} from "@chakra-ui/react";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageSwitcher from "./components/LanguageSwitcher";

export default function Header() {
  const { isOpen, onToggle } = useDisclosure();
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract language from current path
  const getCurrentLanguage = () => {
    const pathSegments = location.pathname.split('/');
    const langFromPath = pathSegments[1];
    return (langFromPath === 'en' || langFromPath === 'es') ? langFromPath : 'en';
  };
  
  const currentLanguage = getCurrentLanguage();

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  const handleNavigation = (path: string) => {
    navigate(getLocalizedPath(path));
  };

  return (
    <Box
      border="4px solid black" // Black border on all sides
      width="100%"
      position="fixed"
      top={0}
      left={0}
      zIndex={20}
      bg="white"
      pr="5%"
      pl="5%"
      height="10%"
    >
      {/* Main Header */}
      <Flex
        as="header"
        justifyContent="space-between"
        alignItems="center"
        height="100%"
        maxW='1400px'
        py={4}
        margin="0 auto"
        px={{ base: 6, md: 3, lg: 6 }}
      >
        {/* Site Title */}
        <Link
          onClick={() => handleNavigation('/')}
          fontSize={{ base: "1.2rem", md: "1.1rem", lg: "1.7rem" }}
          fontWeight='bold'
          fontFamily="Abel"
          _hover={{ textDecoration: "none" }}
          cursor="pointer"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          Al grano con Pablo
        </Link>

        {/* Desktop Navigation Links */}
        <HStack
          display={{ base: "none", md: "flex" }}
          spacing={{ base: 6, md: 2, lg: 6 }}
          fontWeight="medium"
          fontSize={{ base: "lg", md: "sm", lg: "lg" }}
          fontFamily="Source Code Pro"
          alignItems="center"
        >
          <Link 
            onClick={() => handleNavigation('/')} 
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('work')}
          </Link>
          <Link
            onClick={() => handleNavigation('/prints')}
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('prints')}
          </Link>
          <Link
            onClick={() => handleNavigation('/services')}
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('services')}
          </Link>
          <Link 
            onClick={() => handleNavigation('/about')} 
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('about')}
          </Link>
          <Link
            onClick={() => handleNavigation('/tools')}
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('tools')}
          </Link>
          <Link
            onClick={() => handleNavigation('/blog')}
            _hover={{ textDecoration: "underline" }}
            cursor="pointer"
          >
            {t('blog')}
          </Link>

          <LanguageSwitcher />
        </HStack>

        {/* Mobile Hamburger Menu */}
        <IconButton
          display={{ base: "flex", md: "none" }}
          aria-label={isOpen ? "Close Menu" : "Open Menu"}
          icon={isOpen ? <CloseIcon boxSize={4} /> : <HamburgerIcon boxSize={6} />}
          onClick={onToggle}
          variant="ghost"
        />
      </Flex>

      {/* Mobile Fullscreen Menu Overlay */}
      {isOpen && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100vh"
          bg="white"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          zIndex={10}
          border="8px solid black" // Black border around the overlay
        >
          <VStack spacing={8}>
            <Link
              onClick={() => {
                handleNavigation('/');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('work')}
            </Link>
            <Link
              onClick={() => {
                handleNavigation('/prints');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('prints')}
            </Link>
            <Link
              onClick={() => {
                handleNavigation('/services');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('services')}
            </Link>
            <Link
              onClick={() => {
                handleNavigation('/about');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('about')}
            </Link>
            <Link
              onClick={() => {
                handleNavigation('/tools');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('tools')}
            </Link>
            <Link
              onClick={() => {
                handleNavigation('/blog');
                onToggle();
              }}
              fontSize="2xl"
              fontWeight="medium"
              letterSpacing="wide"
              fontFamily="Source Code Pro"
              _hover={{ textDecoration: "underline" }}
              cursor="pointer"
            >
              {t('blog')}
            </Link>

            <LanguageSwitcher onLanguageChange={onToggle} />
          </VStack>
        </Flex>
      )}
    </Box>
  );
}
